'use client';

import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { runWhenIdle } from '../lib/browserSurface';
import { afterEngagement } from '../lib/afterEngagement';
import { numberField, readJson } from '../lib/jsonBody';

interface YandexMetrikaState {
  counterId: number | null;
  isReady: boolean;
}

const initialState: YandexMetrikaState = {
  counterId: null,
  isReady: false,
};

let cachedState: YandexMetrikaState = initialState;
let initPromise: Promise<void> | null = null;
const subscribers = new Set<(s: YandexMetrikaState) => void>();

function publish(next: YandexMetrikaState) {
  cachedState = next;
  subscribers.forEach((cb) => cb(next));
}

function ensureInited() {
  if (initPromise) return initPromise;

  const baseUrl = getApiBaseUrl();

  initPromise = fetch(`${baseUrl}/api/public/config`)
    .then(readJson)
    .then((data) => {
      const counterId = numberField(data, 'yandexMetrikaCounterId');
      if (counterId === undefined) {
        publish({ counterId: null, isReady: true });
        return;
      }

      const counter: YandexMetrikaCounter =
        window.ym ??
        Object.assign(
          function queueCall(...args: unknown[]): void {
            (counter.a ??= []).push(args);
          },
          { l: Date.now() },
        );
      window.ym = counter;

      publish({ counterId, isReady: true });

      // WHY: clickmap выключена: слала ~2 млн запросов /clmap/ в сутки (~35 на сессию) поверх мобильного 4G
      counter(counterId, 'init', {
        clickmap: false,
        trackLinks: true,
        accurateTrackBounce: true,
        ecommerce: 'dataLayer',
      });

      // WHY: tag.js (89 КБ) попадал в окно TBT; init и события лежат в очереди стаба и уйдут после загрузки
      afterEngagement(() => {
        const script = document.createElement('script');
        script.src = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;
        script.async = true;
        document.head.appendChild(script);
      });
    })
    .catch(() => {
      publish({ counterId: null, isReady: true });
    });

  return initPromise;
}

export function useYandexMetrika(): YandexMetrikaState {
  const [state, setState] = useState<YandexMetrikaState>(cachedState);

  useEffect(() => {
    subscribers.add(setState);
    setState(cachedState);
    const cancel = runWhenIdle(
      () => {
        void ensureInited();
      },
      { timeoutMs: 3000, fallbackDelayMs: 1500 },
    );
    return () => {
      subscribers.delete(setState);
      cancel();
    };
  }, []);

  return state;
}
